import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  alpha,
} from '@mui/material';
import {
  Visibility as ImpressionsIcon,
  TouchApp as ClicksIcon,
  PersonAdd as UsersIcon,
  ContactMail as LeadsIcon,
} from '@mui/icons-material';
import {
  FunnelChart,
  Funnel,
  LabelList,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import ReactECharts from 'echarts-for-react';

const funnelColors = {
  primary: '#45B7D1',    // Azul vibrante
  secondary: '#4ECDC4',  // Teal vibrante
  success: '#42C767',    // Verde vibrante
  warning: '#FFB347',    // Naranja vibrante
  danger: '#FF6B6B',     // Rojo vibrante
  purple: '#9B59B6',     // Morado vibrante
  gradient: {
    blue: ['#45B7D1', '#4ECDC4'],
    vibrant: ['#FF6B6B', '#FFB347', '#4ECDC4', '#45B7D1', '#42C767'],
  },
};

// Hiring/Recruitment Funnel - Vibrant theme
const hiringFunnelData = [
  { 
    stage: 'Applied', 
    value: 120,
    displayValue: '120',
    fill: '#FF6B6B', // Vibrant red
  },
  { 
    stage: 'Resume Qualified', 
    value: 63,
    displayValue: '63',
    conversionRate: '52.5%',
    fill: '#FFB347', // Vibrant orange
  },
  { 
    stage: 'Initial Approach', 
    value: 38,
    displayValue: '38',
    conversionRate: '60.3%',
    fill: '#4ECDC4', // Vibrant teal
  },
  { 
    stage: 'Interview', 
    value: 24,
    displayValue: '24',
    conversionRate: '63.2%',
    fill: '#45B7D1', // Vibrant blue
  },
  { 
    stage: 'Hired', 
    value: 8,
    displayValue: '8',
    conversionRate: '33.3%',
    fill: '#42C767', // Vibrant green
  },
];

const CustomLabel = (props) => {
  const { x, y, width, height, value, payload } = props;
  
  if (!payload) return null;
  
  return (
    <g>
      {/* Número principal centrado */}
      <text
        x={x + width / 2}
        y={y + height / 2}
        fill="white"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="40"
        fontWeight="bold"
      >
        {payload.displayValue || value}
      </text>
      
      {/* Información externa a la derecha */}
      <g>
        {/* Nombre de la etapa */}
        <text
          x={x + width + 30}
          y={y + height / 2 - 10}
          fill="#1a1a1a"
          textAnchor="start"
          fontSize="18"
          fontWeight="600"
        >
          {payload.stage}
        </text>
        
        {/* Tasa de conversión */}
        {payload.conversionRate && (
          <text
            x={x + width + 30}
            y={y + height / 2 + 15}
            fill="#666"
            textAnchor="start"
            fontSize="16"
            fontWeight="500"
          >
            {payload.conversionRate}
          </text>
        )}
      </g>
    </g>
  );
};

const MetricCard = ({ title, value, subtitle, icon: Icon, color, description }) => (
  <Paper sx={{ 
    p: 3, 
    height: '100%',
    background: `linear-gradient(135deg, ${alpha(color, 0.1)}, ${alpha(color, 0.05)})`,
    borderRadius: 3,
    border: `1px solid ${alpha(color, 0.2)}`,
    transition: 'all 0.3s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: `0 8px 24px ${alpha(color, 0.15)}`,
    },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
      <Box sx={{
        width: 48,
        height: 48,
        borderRadius: 2,
        background: `linear-gradient(135deg, ${alpha(color, 0.2)}, ${alpha(color, 0.1)})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 24, color }} />
      </Box>
      <Box>
        <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.75rem' }}>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, color, lineHeight: 1 }}>
          {value}
        </Typography>
      </Box>
    </Box>
    {subtitle && (
      <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem', mb: 0.5 }}>
        {subtitle}
      </Typography>
    )}
    {description && (
      <Typography variant="caption" sx={{ color: '#999', fontSize: '0.75rem' }}>
        {description}
      </Typography>
    )}
  </Paper>
);

const FunnelShowcase = () => {
  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      p: { xs: 2, md: 4 }
    }}>
      {/* Hero Header */}
      <Box sx={{ mb: 5, textAlign: 'center' }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 800, 
            mb: 2,
            background: `linear-gradient(135deg, ${funnelColors.danger} 0%, ${funnelColors.primary} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Conversion Funnel Analytics
        </Typography>
        <Typography variant="h6" sx={{ color: '#666', fontWeight: 400, mb: 3 }}>
          Visualize your conversion paths and identify optimization opportunities
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Chip label="Recruitment Pipeline" sx={{ bgcolor: funnelColors.danger, color: 'white' }} />
          <Chip label="Multiple Views" sx={{ bgcolor: funnelColors.warning, color: 'white' }} />
          <Chip label="Conversion Rates" sx={{ bgcolor: funnelColors.primary, color: 'white' }} />
        </Box>
      </Box>

      {/* Hiring Statistics */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Hiring Metrics
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 3, 
            textAlign: 'center',
            background: `linear-gradient(135deg, ${alpha('#FF6B6B', 0.15)}, ${alpha('#FF6B6B', 0.05)})`,
            borderRadius: 3,
            border: `2px solid ${alpha('#FF6B6B', 0.3)}`,
            boxShadow: `0 4px 20px ${alpha('#FF6B6B', 0.15)}`,
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 30px ${alpha('#FF6B6B', 0.25)}`,
            },
          }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#FF6B6B', mb: 1 }}>
              120
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', fontWeight: 600 }}>
              Applications Received
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 3, 
            textAlign: 'center',
            background: `linear-gradient(135deg, ${alpha('#FFB347', 0.15)}, ${alpha('#FFB347', 0.05)})`,
            borderRadius: 3,
            border: `2px solid ${alpha('#FFB347', 0.3)}`,
            boxShadow: `0 4px 20px ${alpha('#FFB347', 0.15)}`,
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 30px ${alpha('#FFB347', 0.25)}`,
            },
          }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFB347', mb: 1 }}>
              52.5%
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', fontWeight: 600 }}>
              Resume Pass Rate
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 3, 
            textAlign: 'center',
            background: `linear-gradient(135deg, ${alpha('#45B7D1', 0.15)}, ${alpha('#45B7D1', 0.05)})`,
            borderRadius: 3,
            border: `2px solid ${alpha('#45B7D1', 0.3)}`,
            boxShadow: `0 4px 20px ${alpha('#45B7D1', 0.15)}`,
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 30px ${alpha('#45B7D1', 0.25)}`,
            },
          }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#45B7D1', mb: 1 }}>
              63.2%
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', fontWeight: 600 }}>
              Interview Conversion
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 3, 
            textAlign: 'center',
            background: `linear-gradient(135deg, ${alpha('#42C767', 0.15)}, ${alpha('#42C767', 0.05)})`,
            borderRadius: 3,
            border: `2px solid ${alpha('#42C767', 0.3)}`,
            boxShadow: `0 4px 20px ${alpha('#42C767', 0.15)}`,
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 30px ${alpha('#42C767', 0.25)}`,
            },
          }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#42C767', mb: 1 }}>
              8
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', fontWeight: 600 }}>
              Successful Hires
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Hiring Recruitment Funnel */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Hiring & Recruitment Funnel
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 4,
              borderRadius: 3,
              overflow: 'hidden',
              background: '#ffffff',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e8e8e8',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
                Recruitment Pipeline
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                From application to hire - tracking candidate conversion at each stage
              </Typography>
            </Box>
            
            {/* Improved Funnel Chart */}
            <Box sx={{ mb: 4 }}>
              <ReactECharts
                option={{
                  tooltip: {
                    trigger: 'item',
                    formatter: (params) => {
                      const data = hiringFunnelData[params.dataIndex];
                      return `
                        <div style="padding: 10px;">
                          <strong>${params.name}</strong><br/>
                          Count: ${params.value}<br/>
                          ${data.conversionRate ? `Conversion: ${data.conversionRate}` : ''}
                        </div>
                      `;
                    },
                  },
                  series: [
                    {
                      name: 'Recruitment',
                      type: 'funnel',
                      left: '10%',
                      top: 20,
                      bottom: 20,
                      width: '60%',
                      min: 0,
                      max: 120,
                      minSize: '20%',
                      maxSize: '100%',
                      sort: 'descending',
                      gap: 2,
                      label: {
                        show: true,
                        position: 'inside',
                        formatter: (params) => {
                          return `{value|${params.value}}\n{name|${params.name}}`;
                        },
                        rich: {
                          value: {
                            fontSize: 36,
                            fontWeight: 'bold',
                            color: '#fff',
                          },
                          name: {
                            fontSize: 18,
                            color: '#fff',
                            padding: [5, 0, 0, 0],
                          },
                        },
                      },
                      labelLine: {
                        show: false,
                      },
                      itemStyle: {
                        borderColor: '#fff',
                        borderWidth: 2,
                      },
                      emphasis: {
                        label: {
                          fontSize: 20,
                        },
                      },
                      data: hiringFunnelData.map((stage) => ({
                        value: stage.value,
                        name: stage.stage,
                        itemStyle: { color: stage.fill },
                      })),
                    },
                  ],
                  // Añadir gráfico de texto para mostrar porcentajes al lado
                  graphic: hiringFunnelData
                    .map((stage, index) => ({
                      type: 'text',
                      right: '25%',
                      top: 50 + (index * 115),
                      style: {
                        text: stage.conversionRate || (index === 0 ? '100%' : ''),
                        fontSize: 20,
                        fontWeight: 'bold',
                        fill: stage.fill,
                      },
                    }))
                    .filter(item => item.style.text !== ''),
                }}
                style={{ height: '600px' }}
              />
            </Box>

            {/* Alternative Table View */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#666' }}>
                Detailed Breakdown
              </Typography>
              <Grid container spacing={2}>
                {hiringFunnelData.map((stage, index) => (
                  <Grid item xs={12} key={stage.stage}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      p: 2,
                      borderRadius: 2,
                      background: `linear-gradient(90deg, ${alpha(stage.fill, 0.1)}, transparent)`,
                      border: `1px solid ${alpha(stage.fill, 0.3)}`,
                    }}>
                      <Box sx={{
                        minWidth: 80,
                        textAlign: 'center',
                      }}>
                        <Typography variant="h3" sx={{ fontWeight: 800, color: stage.fill }}>
                          {stage.displayValue}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                            {stage.stage}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            {((stage.value / hiringFunnelData[0].value) * 100).toFixed(1)}% of total applicants
                          </Typography>
                      </Box>
                      {(stage.conversionRate || index === 0) && (
                        <Box sx={{
                          bgcolor: stage.fill,
                          color: 'white',
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          fontWeight: 600,
                        }}>
                          {stage.conversionRate || '100%'}
                        </Box>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ECharts Recruitment Funnel */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Improved Recruitment Pipeline Views
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 4, borderRadius: 3, background: '#ffffff', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
              Clear Funnel View
            </Typography>
            <ReactECharts
              option={{
                tooltip: {
                  trigger: 'item',
                  formatter: (params) => {
                    const data = hiringFunnelData[params.dataIndex];
                    return `
                      <div style="padding: 10px;">
                        <strong>${params.name}</strong><br/>
                        Count: ${params.value}<br/>
                        ${data.conversionRate ? `Conversion: ${data.conversionRate}` : ''}
                      </div>
                    `;
                  },
                },
                series: [
                  {
                    name: 'Recruitment',
                    type: 'funnel',
                    left: '10%',
                    top: 60,
                    bottom: 60,
                    width: '80%',
                    min: 0,
                    max: 120,
                    minSize: '20%',
                    maxSize: '100%',
                    sort: 'descending',
                    gap: 5,
                    label: {
                      show: true,
                      position: 'outside',
                      formatter: (params) => {
                        const data = hiringFunnelData[params.dataIndex];
                        const rate = data.conversionRate || (params.dataIndex === 0 ? '100%' : '');
                        return `{name|${params.name}}\n{value|${params.value}} {rate|${rate}}`;
                      },
                      rich: {
                        name: {
                          fontSize: 16,
                          fontWeight: 'bold',
                          color: '#333',
                        },
                        value: {
                          fontSize: 24,
                          fontWeight: 'bold',
                          color: '#1a1a1a',
                        },
                        rate: {
                          fontSize: 14,
                          color: '#666',
                        },
                      },
                    },
                    labelLine: {
                      length: 20,
                      lineStyle: {
                        width: 2,
                        type: 'solid',
                      },
                    },
                    itemStyle: {
                      borderColor: '#fff',
                      borderWidth: 2,
                    },
                    emphasis: {
                      label: {
                        fontSize: 20,
                      },
                    },
                    data: hiringFunnelData.map((stage) => ({
                      value: stage.value,
                      name: stage.stage,
                      itemStyle: { color: stage.fill },
                    })),
                  },
                ],
              }}
              style={{ height: '400px' }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 4, borderRadius: 3, background: '#ffffff', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
              Bar Chart Alternative
            </Typography>
            <ReactECharts
              option={{
                tooltip: {
                  trigger: 'axis',
                  axisPointer: { type: 'shadow' },
                },
                grid: {
                  left: '3%',
                  right: '4%',
                  bottom: '3%',
                  containLabel: true,
                },
                xAxis: {
                  type: 'value',
                  max: 120,
                },
                yAxis: {
                  type: 'category',
                  data: hiringFunnelData.map(d => d.stage).reverse(),
                },
                series: [
                  {
                    name: 'Candidates',
                    type: 'bar',
                    data: hiringFunnelData.map((stage) => ({
                      value: stage.value,
                      itemStyle: { color: stage.fill },
                      label: {
                        show: true,
                        position: 'right',
                        formatter: (params) => {
                          const data = hiringFunnelData[hiringFunnelData.length - 1 - params.dataIndex];
                          const dataIndex = hiringFunnelData.length - 1 - params.dataIndex;
                          const rate = data.conversionRate || (dataIndex === 0 ? '100%' : '');
                          return `${params.value} ${rate ? `(${rate})` : ''}`;
                        },
                        fontSize: 14,
                        fontWeight: 'bold',
                      },
                    })).reverse(),
                  },
                ],
              }}
              style={{ height: '400px' }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Alternative Recruitment Pipeline Visualizations */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Alternative Recruitment Pipeline Views
      </Typography>

      {/* Horizontal Cards Pipeline */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 4, borderRadius: 3, background: '#ffffff', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
              Horizontal Pipeline View
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, overflowX: 'auto', pb: 2 }}>
              {hiringFunnelData.map((stage, index) => (
                <React.Fragment key={stage.stage}>
                  <Box sx={{ minWidth: 180 }}>
                    <Paper sx={{
                      p: 3,
                      textAlign: 'center',
                      background: `linear-gradient(135deg, ${alpha(stage.fill, 0.15)}, ${alpha(stage.fill, 0.05)})`,
                      border: `3px solid ${stage.fill}`,
                      borderRadius: 3,
                      position: 'relative',
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: `0 12px 40px ${alpha(stage.fill, 0.3)}`,
                      },
                    }}>
                      <Typography variant="h2" sx={{ fontWeight: 800, color: stage.fill, mb: 1 }}>
                        {stage.displayValue}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                        {stage.stage}
                      </Typography>
                      {stage.conversionRate && (
                        <Chip 
                          label={stage.conversionRate}
                          size="small"
                          sx={{ 
                            bgcolor: stage.fill,
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                          }}
                        />
                      )}
                    </Paper>
                  </Box>
                  {index < hiringFunnelData.length - 1 && (
                    <Box sx={{ 
                      fontSize: 48, 
                      color: alpha('#666', 0.3),
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      →
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Vertical Steps Pipeline */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4, borderRadius: 3, background: '#ffffff', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
              Vertical Steps View
            </Typography>
            <Box sx={{ position: 'relative' }}>
              {hiringFunnelData.map((stage, index) => (
                <Box key={stage.stage} sx={{ position: 'relative', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Box sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: stage.fill,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 800,
                      fontSize: 24,
                      boxShadow: `0 4px 20px ${alpha(stage.fill, 0.3)}`,
                      position: 'relative',
                      zIndex: 1,
                    }}>
                      {index + 1}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Paper sx={{
                        p: 3,
                        background: `linear-gradient(90deg, ${alpha(stage.fill, 0.1)}, transparent)`,
                        border: `2px solid ${alpha(stage.fill, 0.3)}`,
                        borderRadius: 2,
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: stage.fill }}>
                              {stage.stage}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#333' }}>
                              {stage.displayValue}
                            </Typography>
                          </Box>
                          {(stage.conversionRate || index === 0) && (
                            <Box sx={{
                              bgcolor: stage.fill,
                              color: 'white',
                              px: 2,
                              py: 1,
                              borderRadius: 2,
                              fontWeight: 600,
                              fontSize: '1.125rem',
                            }}>
                              {stage.conversionRate || '100%'}
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    </Box>
                  </Box>
                  {index < hiringFunnelData.length - 1 && (
                    <Box sx={{
                      position: 'absolute',
                      left: 29,
                      top: 60,
                      width: 2,
                      height: 48,
                      background: `linear-gradient(180deg, ${stage.fill}, ${hiringFunnelData[index + 1].fill})`,
                    }} />
                  )}
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Compact Cards View */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4, borderRadius: 3, background: '#ffffff', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
              Compact Cards View
            </Typography>
            <Grid container spacing={2}>
              {hiringFunnelData.map((stage, index) => (
                <Grid item xs={12} key={stage.stage}>
                  <Box sx={{
                    p: 2.5,
                    background: stage.fill,
                    color: 'white',
                    borderRadius: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateX(8px)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, rgba(255,255,255,0.1), transparent)',
                    },
                  }}>
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {stage.stage}
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 800 }}>
                        {stage.displayValue}
                      </Typography>
                    </Box>
                    <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'right' }}>
                      {(stage.conversionRate || index === 0) && (
                        <>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            Conversion Rate
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {stage.conversionRate || '100%'}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* True Pyramid View */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 4, borderRadius: 3, background: '#ffffff', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
              Classic Pyramid View
            </Typography>
            <Box sx={{ position: 'relative', height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="100%" height="100%" viewBox="0 0 800 400" style={{ maxWidth: '800px' }}>
                {hiringFunnelData.map((stage, index) => {
                  const totalHeight = 380;
                  const levelHeight = totalHeight / hiringFunnelData.length;
                  const y = index * levelHeight + 10;
                  
                  // Cálculo para forma de pirámide real invertida (más ancho arriba)
                  const maxWidth = 700;
                  const minWidth = 100;
                  const widthStep = (maxWidth - minWidth) / (hiringFunnelData.length - 1);
                  
                  const topWidth = maxWidth - (index * widthStep);
                  const bottomWidth = maxWidth - ((index + 1) * widthStep);
                  const centerX = 400;
                  
                  const topLeft = centerX - topWidth / 2;
                  const topRight = centerX + topWidth / 2;
                  const bottomLeft = centerX - bottomWidth / 2;
                  const bottomRight = centerX + bottomWidth / 2;
                  
                  return (
                    <g key={stage.stage}>
                      {/* Trapecio para cada nivel */}
                      <path
                        d={`
                          M ${topLeft} ${y}
                          L ${topRight} ${y}
                          L ${bottomRight} ${y + levelHeight}
                          L ${bottomLeft} ${y + levelHeight}
                          Z
                        `}
                        fill={stage.fill}
                        stroke="#fff"
                        strokeWidth="2"
                        style={{ cursor: 'pointer' }}
                      />
                      
                      {/* Texto centrado */}
                      <text
                        x={centerX}
                        y={y + levelHeight / 2 - 10}
                        textAnchor="middle"
                        fill="white"
                        fontSize="32"
                        fontWeight="bold"
                      >
                        {stage.displayValue}
                      </text>
                      <text
                        x={centerX}
                        y={y + levelHeight / 2 + 15}
                        textAnchor="middle"
                        fill="white"
                        fontSize="18"
                        fontWeight="500"
                      >
                        {stage.stage}
                      </text>
                      
                      {/* Porcentaje al lado */}
                      {stage.conversionRate && (
                        <text
                          x={bottomRight + 20}
                          y={y + levelHeight / 2 + 5}
                          textAnchor="start"
                          fill={stage.fill}
                          fontSize="16"
                          fontWeight="600"
                        >
                          {stage.conversionRate}
                        </text>
                      )}
                    </g>
                  );
                })}
              </svg>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Sankey Flow View */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 4, borderRadius: 3, background: '#ffffff', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
              Recruitment Flow Diagram
            </Typography>
            <ReactECharts
              option={{
                tooltip: {
                  trigger: 'item',
                  triggerOn: 'mousemove',
                },
                series: [
                  {
                    type: 'sankey',
                    layout: 'none',
                    emphasis: {
                      focus: 'adjacency',
                    },
                    data: [
                      { name: 'Applied', itemStyle: { color: '#FF6B6B' } },
                      { name: 'Resume Qualified', itemStyle: { color: '#FFB347' } },
                      { name: 'Initial Approach', itemStyle: { color: '#4ECDC4' } },
                      { name: 'Interview', itemStyle: { color: '#45B7D1' } },
                      { name: 'Hired', itemStyle: { color: '#42C767' } },
                      { name: 'Rejected at Resume', itemStyle: { color: '#E0E0E0' } },
                      { name: 'Rejected at Approach', itemStyle: { color: '#E0E0E0' } },
                      { name: 'Rejected at Interview', itemStyle: { color: '#E0E0E0' } },
                      { name: 'Rejected at Final', itemStyle: { color: '#E0E0E0' } },
                    ],
                    links: [
                      { source: 'Applied', target: 'Resume Qualified', value: 63 },
                      { source: 'Applied', target: 'Rejected at Resume', value: 57 },
                      { source: 'Resume Qualified', target: 'Initial Approach', value: 38 },
                      { source: 'Resume Qualified', target: 'Rejected at Approach', value: 25 },
                      { source: 'Initial Approach', target: 'Interview', value: 24 },
                      { source: 'Initial Approach', target: 'Rejected at Interview', value: 14 },
                      { source: 'Interview', target: 'Hired', value: 8 },
                      { source: 'Interview', target: 'Rejected at Final', value: 16 },
                    ],
                    lineStyle: {
                      color: 'source',
                      curveness: 0.5,
                    },
                    label: {
                      show: true,
                      formatter: '{b}: {c}',
                      fontSize: 14,
                    },
                  },
                ],
              }}
              style={{ height: '500px' }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Pyramid and Circular Progress Views */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        {/* Pyramid View */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4, borderRadius: 3, background: '#ffffff', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
              Pyramid Chart
            </Typography>
            <ReactECharts
              option={{
                tooltip: {
                  trigger: 'item',
                  formatter: (params) => {
                    const data = hiringFunnelData[params.dataIndex];
                    return `
                      <div style="padding: 10px;">
                        <strong>${params.name}</strong><br/>
                        Count: ${params.value}<br/>
                        ${data.conversionRate ? `Conversion: ${data.conversionRate}` : ''}
                      </div>
                    `;
                  },
                },
                series: [
                  {
                    name: 'Recruitment Pyramid',
                    type: 'funnel',
                    left: '10%',
                    top: '10%',
                    bottom: '10%',
                    width: '80%',
                    min: 0,
                    max: 120,
                    minSize: '0%',
                    maxSize: '100%',
                    sort: 'descending',
                    gap: 0,
                    shape: 'pyramid',
                    label: {
                      show: true,
                      position: 'center',
                      formatter: (params) => {
                        const data = hiringFunnelData[params.dataIndex];
                        return `{name|${params.name}}\n{value|(${params.value})}`;
                      },
                      rich: {
                        name: {
                          fontSize: 18,
                          fontWeight: 'bold',
                          color: '#fff',
                        },
                        value: {
                          fontSize: 24,
                          fontWeight: 'bold',
                          color: '#fff',
                          padding: [5, 0, 0, 0],
                        },
                      },
                    },
                    labelLine: {
                      show: false,
                    },
                    itemStyle: {
                      borderColor: '#fff',
                      borderWidth: 0,
                    },
                    emphasis: {
                      label: {
                        fontSize: 20,
                      },
                    },
                    data: hiringFunnelData.map((stage) => ({
                      value: stage.value,
                      name: stage.stage,
                      itemStyle: { color: stage.fill },
                    })),
                  },
                ],
              }}
              style={{ height: '400px' }}
            />
            
            {/* Conversion rates on the side */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', mb: 1 }}>
                Conversion Rates
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                {hiringFunnelData.map((stage, index) => (
                  stage.conversionRate && (
                    <Chip
                      key={stage.stage}
                      label={`${stage.stage}: ${stage.conversionRate}`}
                      size="small"
                      sx={{
                        bgcolor: alpha(stage.fill, 0.15),
                        color: stage.fill,
                        fontWeight: 600,
                      }}
                    />
                  )
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Circular Progress View */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4, borderRadius: 3, background: '#ffffff', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
              Circular Progress View
            </Typography>
            <Grid container spacing={2}>
              {hiringFunnelData.map((stage, index) => {
                const percentage = (stage.value / hiringFunnelData[0].value) * 100;
                return (
                  <Grid item xs={12} sm={6} key={stage.stage}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Box
                        sx={{
                          position: 'relative',
                          display: 'inline-flex',
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            background: `conic-gradient(${stage.fill} ${percentage}%, #f0f0f0 ${percentage}%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              width: '85%',
                              height: '85%',
                              borderRadius: '50%',
                              background: 'white',
                            },
                          }}
                        >
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 800,
                              color: stage.fill,
                              position: 'relative',
                              zIndex: 1,
                            }}
                          >
                            {stage.displayValue}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: stage.fill }}>
                        {stage.stage}
                      </Typography>
                      {stage.conversionRate && (
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {stage.conversionRate} conversion
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Horizontal Funnels */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: '#1a1a1a' }}>
        Horizontal Funnels
      </Typography>
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} lg={6}>
          <Paper
            sx={{
              p: 4,
              borderRadius: 3,
              background: '#ffffff',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e8e8e8',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
                Horizontal Funnel
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Left to right conversion flow
              </Typography>
            </Box>
            
            <ReactECharts
              option={{
                tooltip: {
                  trigger: 'item',
                  formatter: '{b}: {c}',
                },
                series: [
                  {
                    name: 'Funnel',
                    type: 'funnel',
                    orient: 'horizontal',
                    left: '5%',
                    top: '10%',
                    bottom: '10%',
                    width: '90%',
                    min: 0,
                    max: 100,
                    gap: 0,
                    label: {
                      show: true,
                      position: 'inside',
                      formatter: (params) => `{white|${params.value}}\n{name|${params.name}}`,
                      rich: {
                        white: {
                          fontSize: 28,
                          fontWeight: 'bold',
                          color: '#fff',
                        },
                        name: {
                          fontSize: 16,
                          color: 'rgba(255,255,255,0.9)',
                          padding: [5, 0, 0, 0],
                        },
                      },
                    },
                    labelLine: {
                      show: false,
                    },
                    itemStyle: {
                      borderWidth: 0,
                    },
                    data: [
                      { value: 23600, name: 'Purchased Device', itemStyle: { color: '#4ECDC4' } },
                      { value: 15200, name: 'Activated Device', itemStyle: { color: '#FF6B6B' } },
                      { value: 10400, name: 'First Session', itemStyle: { color: '#FFB347' } },
                      { value: 5380, name: 'Invite Friend', itemStyle: { color: '#45B7D1' } },
                    ],
                  },
                ],
              }}
              style={{ height: '300px' }}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper
            sx={{
              p: 4,
              borderRadius: 3,
              background: '#ffffff',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e8e8e8',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
                Horizontal Funnel 3D
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                3D perspective with depth effect
              </Typography>
            </Box>
            
            <ReactECharts
              option={{
                tooltip: {
                  trigger: 'item',
                  formatter: '{b}: {c}%',
                },
                series: [
                  {
                    name: 'Funnel 3D',
                    type: 'funnel',
                    orient: 'horizontal',
                    left: '5%',
                    top: '10%',
                    bottom: '10%',
                    width: '90%',
                    min: 0,
                    max: 100,
                    gap: 2,
                    label: {
                      show: true,
                      position: 'inside',
                      formatter: (params) => `{white|${params.value}%}\n{name|${params.name}}`,
                      rich: {
                        white: {
                          fontSize: 26,
                          fontWeight: 'bold',
                          color: '#fff',
                        },
                        name: {
                          fontSize: 14,
                          color: 'rgba(255,255,255,0.9)',
                          padding: [5, 0, 0, 0],
                        },
                      },
                    },
                    labelLine: {
                      show: false,
                    },
                    itemStyle: {
                      borderWidth: 2,
                      borderColor: 'rgba(0,0,0,0.2)',
                      shadowBlur: 10,
                      shadowColor: 'rgba(0, 0, 0, 0.3)',
                      shadowOffsetX: 3,
                      shadowOffsetY: 5,
                    },
                    emphasis: {
                      itemStyle: {
                        shadowBlur: 15,
                        shadowColor: 'rgba(0, 0, 0, 0.4)',
                      },
                    },
                    data: [
                      { value: 100.0, name: 'Site visits', itemStyle: { color: '#42C767' } },
                      { value: 70.0, name: 'Seen ads', itemStyle: { color: '#FF6B6B' } },
                      { value: 78.6, name: 'Clicked ads', itemStyle: { color: '#FFB347' } },
                      { value: 63.6, name: 'Purchased product', itemStyle: { color: '#45B7D1' } },
                    ],
                  },
                ],
              }}
              style={{ height: '300px' }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Footer */}
      <Box sx={{ mt: 6, p: 4, textAlign: 'center', bgcolor: 'rgba(255, 255, 255, 0.9)', borderRadius: 3, border: `1px solid ${alpha(funnelColors.primary, 0.1)}` }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: funnelColors.primary }}>
          📈 Funnel Visualization with Recharts
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Professional funnel charts to visualize conversion paths and identify bottlenecks in your user journey.
          Track every step from initial awareness to final conversion.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
          <Chip label="Multi-Stage Analysis" size="small" sx={{ bgcolor: funnelColors.primary, color: 'white' }} />
          <Chip label="Conversion Rates" size="small" sx={{ bgcolor: funnelColors.success, color: 'white' }} />
          <Chip label="Real Metrics" size="small" sx={{ bgcolor: funnelColors.warning, color: 'white' }} />
          <Chip label="Interactive" size="small" sx={{ bgcolor: funnelColors.danger, color: 'white' }} />
        </Box>
      </Box>
    </Box>
  );
};

export default FunnelShowcase;

